<template>
  <aside class="sidebar-nav">
    <div class="sidebar-logo" @click="goHome">
      <img src="/static/logo.png" :alt="$t('brand_name')" />
      <span>{{ $t('brand_name') }}</span>
    </div>
    <nav class="sidebar-menu">
      <router-link
        v-for="item in menuItems"
        :key="item.name"
        :to="item.path"
        :class="['sidebar-item', { active: isActive(item) }]"
        @click.native="handleMenuClick(item)"
      >
        <van-icon :name="item.icon" size="20" />
        <span>{{ $t(item.i18nKey) }}</span>
        <span v-if="item.name === 'messages' && unreadChatBadge" class="sidebar-unread-badge">{{ unreadChatBadge }}</span>
        <span v-else-if="item.name === 'messages' && $store.state.hasUnreadMsg" class="sidebar-unread-dot"></span>
        <span v-if="item.name === 'matches' && unreadMatchBadge" class="sidebar-unread-badge">{{ unreadMatchBadge }}</span>
        <span v-else-if="item.name === 'matches' && $store.state.hasUnreadMatch" class="sidebar-unread-dot"></span>
      </router-link>
    </nav>

    <!-- 发起 AI 需求澄清的显眼入口（client 底部导航中央「+」的 web 等价物；对齐小红书网页版：发布按钮放菜单下方） -->
    <button v-if="aiDirectChatEnabled" type="button" class="sidebar-ai-btn" @click="goAiChat">
      <van-icon name="plus" size="16" />
      <span>{{ $t('sidebar_publish') }}</span>
    </button>

    <!-- 下载App提示 -->
    <div class="sidebar-download">
      <div class="download-toggle" @click="handleDownloadToggle">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 16px; height: 16px;">
          <path d="M12 18V3M12 18L7 13M12 18L17 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M3 15V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>{{ $t('get_the_app') }}</span>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="download-arrow" :class="{ open: showDownloadMenu }" style="width: 12px; height: 12px;">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="download-dropdown" v-show="showDownloadMenu">
        <a :href="appStoreUrl" target="_blank" class="store-link" @click="handleDownloadClick">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 18px; height: 18px; color: #1A1A1A;">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.21-1.98 1.07-3.11-1.05.05-2.31.7-3.06 1.53-.68.74-1.26 1.93-1.1 3.07 1.18.09 2.38-.59 3.09-1.49z" />
          </svg>
          <span class="store-name">{{ $t('app_store') }}</span>
          <span class="store-desc">{{ $t('desc_iphone_ipad') }}</span>
          <svg viewBox="0 0 24 24" fill="none" style="width: 14px; height: 14px; color: #ccc; margin-left: auto;">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
        <a v-if="!isCnRegion" :href="googlePlayUrl" target="_blank" class="store-link" @click="handleDownloadClick">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 18px; height: 18px; color: #1A1A1A;">
            <path d="M3 20.5V3.5c0-.74.8-1.21 1.45-.88l15.84 8.5c.66.35.66 1.31 0 1.66L4.45 21.38c-.65.33-1.45-.14-1.45-.88z" />
          </svg>
          <span class="store-name">{{ $t('google_play') }}</span>
          <span class="store-desc">{{ $t('desc_android') }}</span>
          <svg viewBox="0 0 24 24" fill="none" style="width: 14px; height: 14px; color: #ccc; margin-left: auto;">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
        <a :href="apkUrl" target="_blank" class="store-link" download @click="handleDownloadClick">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 18px; height: 18px; color: #1A1A1A;">
            <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
          </svg>
          <span class="store-name">{{ $t('direct_apk') }}</span>
          <span class="store-desc">{{ $t('desc_android') }}</span>
          <svg viewBox="0 0 24 24" fill="none" style="width: 14px; height: 14px; color: #ccc; margin-left: auto;">
            <path d="M12 15V3M12 15L8 11M12 15L16 11M3 15V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
        <div class="download-footer">
          <svg viewBox="0 0 24 24" fill="none" style="width: 14px; height: 14px; color: #8A8A8A;">
            <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2" />
            <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          {{ $t('search_in_app_stores') }}
        </div>
      </div>
    </div>

    <!-- 微信/小红书等内置浏览器引导遮罩 -->
    <div v-if="showBrowserGuide" class="browser-guide-overlay" @click="showBrowserGuide = false">
      <div class="browser-guide-arrow">
        <svg width="60" height="80" viewBox="0 0 60 80" fill="none">
          <path d="M30 70 L30 20 M30 20 L15 35 M30 20 L45 35" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="browser-guide-text">
        <p style="font-size: 18px; font-weight: 600; margin-bottom: 8px;">{{ guideTitle }}</p>
        <p style="font-size: 14px; line-height: 1.6; opacity: 0.9;">{{ guideText }}</p>
      </div>
    </div>
  </aside>
</template>

<script>
import { AI_DIRECT_CHAT_ENABLED } from '@/assets/feature_flags.js'

export default {
  name: 'SidebarNav',
  data() {
    return {
      showDownloadMenu: false,
      showBrowserGuide: false,
      menuItems: [
        { name: 'channels', path: '/channels', icon: 'friends-o', i18nKey: 'channels_octosay', match: ['/channels', '/channel-'] },
        { name: 'matches', path: '/matches', icon: 'like-o', i18nKey: 'tab_matches', match: ['/matches', '/matched-session'] },
        { name: 'messages', path: '/messages', icon: 'chat-o', i18nKey: 'tab_messages', match: ['/messages'] },
        { name: 'profile', path: '/personal-center', icon: 'user-o', i18nKey: 'tab_profile', match: ['/personal-center', '/settings'] }
      ],
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=ai.octopie.client'
    }
  },
  computed: {
    aiDirectChatEnabled() {
      return AI_DIRECT_CHAT_ENABLED
    },
    // messages tab 总未读数 = 私聊未读会话数 + 未读互动通知条数（99+ 封顶）
    // 数字缺失/为 0 时降级为红点（旧后端兼容）
    unreadChatBadge() {
      const chat = parseInt(this.$store.state.unreadChatCount, 10) || 0
      const inter = parseInt(this.$store.state.unreadInteractionCount, 10) || 0
      const total = chat + inter
      if (total > 99) return '99+'
      return total > 0 ? String(total) : ''
    },
    // matches 菜单未读数（99+ 封顶；为 0 时退回红点，旧后端兼容）
    unreadMatchBadge() {
      const count = parseInt(this.$store.state.unreadMatchCount, 10) || 0
      if (count > 99) return '99+'
      return count > 0 ? String(count) : ''
    },
    isCnRegion() {
      return this.$config && this.$config.region === 'cn'
    },
    appStoreUrl() {
      return this.isCnRegion
        ? 'https://apps.apple.com/cn/app/章摇摇/id6760103197'
        : 'https://apps.apple.com/us/app/octopie-ai/id6760101997'
    },
    apkUrl() {
      return this.isCnRegion
        ? 'https://download.octopie.cn/octopie_cn.apk'
        : 'https://download.octosay.ai/octopie_ai.apk'
    },
    isWeChat() {
      const ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('micromessenger') !== -1
    },
    isXiaohongshu() {
      const ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('xhsdiscover') !== -1 || ua.indexOf('xiaohongshu') !== -1
    },
    isQQ() {
      const ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('qq/') !== -1 && ua.indexOf('mqqbrowser') === -1
    },
    isWeibo() {
      const ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('weibo') !== -1
    },
    isInAppBrowser() {
      const ua = (navigator.userAgent || '').toLowerCase()
      return this.isWeChat || this.isXiaohongshu || this.isQQ || this.isWeibo ||
        ua.indexOf('fbav') !== -1 ||
        ua.indexOf('instagram') !== -1 ||
        ua.indexOf('reddit') !== -1 ||
        ua.indexOf('snssdk') !== -1 ||
        ua.indexOf('bytedance') !== -1 ||
        ua.indexOf('linkedinapp') !== -1 ||
        ua.indexOf('line/') !== -1
    },
    guideTitle() {
      if (this.isWeChat) {
        return this.isCnRegion ? '请在浏览器中打开' : 'Open in Browser'
      }
      return this.isCnRegion ? '请在系统浏览器中打开' : 'Open in System Browser'
    },
    guideText() {
      if (this.isCnRegion) {
        return '点击右上角 "..." 或 "···" 按钮，选择"在浏览器中打开"，即可下载 App'
      }
      return 'Tap the "..." or "···" button in the top right, then select "Open in Browser" to download the app'
    }
  },
  methods: {
    handleMenuClick(item) {
      // 点击 matches 菜单时立即清除红点
      if (item.name === 'matches' && this.$store.state.hasUnreadMatch) {
        this.$store.commit('setHasUnreadMatch', false)
      }
      if (item.name === 'matches' && (parseInt(this.$store.state.unreadMatchCount, 10) || 0) > 0) {
        this.$store.commit('setUnreadMatchCount', 0)
      }
      // 点击 messages 菜单时本地临时清零红点/数字（下次冷启动会重新拉取真实数字）
      if (item.name === 'messages') {
        if (this.$store.state.hasUnreadMsg) {
          this.$store.commit('setHasUnreadMsg', false)
          this.$store.commit('setHasUnreadChatMsg', false)
        }
        if ((parseInt(this.$store.state.unreadChatCount, 10) || 0) > 0) {
          this.$store.commit('setUnreadChatCount', 0)
        }
        if ((parseInt(this.$store.state.unreadInteractionCount, 10) || 0) > 0) {
          this.$store.commit('setUnreadInteractionCount', 0)
        }
      }
    },
    isActive(item) {
      const path = this.$route.path
      if (item.match) {
        return item.match.some(p => path.startsWith(p))
      }
      return path === item.path || path.startsWith(item.path + '/')
    },
    goHome() {
      window.location.href = '/'
    },
    goAiChat() {
      this.showDownloadMenu = false
      // 独立 AI 对话改为全局弹窗（对齐群内 AI 聊天弹窗交互），不再跳 /ai 页
      this.$store.commit('setShowAiChatPopup', true)
    },
    handleDownloadToggle(e) {
      // 微信/小红书：直接弹出引导遮罩，不展开下拉菜单
      if (this.isWeChat || this.isXiaohongshu) {
        if (e) e.preventDefault()
        this.showBrowserGuide = true
        return
      }
      this.showDownloadMenu = !this.showDownloadMenu
    },
    handleDownloadClick(e) {
      // 微信/小红书：拦截点击，弹出引导遮罩
      if (this.isWeChat || this.isXiaohongshu) {
        e.preventDefault()
        this.showBrowserGuide = true
        return
      }
      // 其他 in-app browser：同时展示引导，但允许 <a> 继续尝试跳转
      if (this.isInAppBrowser) {
        this.showBrowserGuide = true
      }
    }
  }
}
</script>

<style scoped>
.sidebar-nav {
  width: 220px;
  height: 100vh;
  background: #fff;
  border-right: 1px solid #E0DDD8;
  display: flex;
  flex-direction: column;
  padding: 24px 12px 12px;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 32px;
  padding-left: 12px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.sidebar-logo:hover {
  opacity: 0.8;
}

.sidebar-logo img {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: block;
  flex-shrink: 0;
}

.sidebar-logo span {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  transform: translateY(1px);
  color: #1A1A1A;
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 发起 AI 需求澄清按钮（对应 client 底部导航中央「+」） */
.sidebar-ai-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 16px 12px 8px;
  padding: 9px 0;
  border: none;
  border-radius: 22px;
  background: #E8543A;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sidebar-ai-btn:hover {
  background: #C93E1A;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  color: #4A4A4A;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
}

.sidebar-item:hover {
  background: #F7F5F2;
  color: #1A1A1A;
}

.sidebar-item.active {
  background: #FEF0ED;
  color: #E8543A;
  font-weight: 600;
}

.sidebar-item i {
  flex-shrink: 0;
}

.sidebar-unread-dot {
  width: 8px;
  height: 8px;
  background: #E8543A;
  border-radius: 50%;
  margin-left: auto;
  flex-shrink: 0;
}

.sidebar-unread-badge {
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 5px;
  background: #E8543A;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 9px;
  text-align: center;
  box-sizing: border-box;
  flex-shrink: 0;
}

.sidebar-download {
  margin-top: 24px;
  position: relative;
  flex-shrink: 0;
}

.download-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: #4A4A4A;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.download-toggle:hover {
  background: #F7F5F2;
}

.download-arrow {
  margin-left: auto;
  transition: transform 0.2s;
}

.download-arrow.open {
  transform: rotate(180deg);
}

.download-dropdown {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #E0DDD8;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
}

.store-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  color: #1A1A1A;
  transition: background 0.2s;
}

.store-link:hover {
  background: #F7F5F2;
}

.store-name {
  font-size: 13px;
  font-weight: 600;
}

.store-desc {
  font-size: 11px;
  color: #8A8A8A;
}

.download-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 11px;
  color: #8A8A8A;
  border-top: 1px solid #F0EDE8;
  margin-top: 4px;
}

/* in-app browser 引导遮罩 */
.browser-guide-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 20001;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 16px 24px;
}

.browser-guide-arrow {
  margin-top: 8px;
  margin-right: 16px;
  animation: bounce 1s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.browser-guide-text {
  margin-top: 16px;
  text-align: right;
  color: #fff;
  max-width: 280px;
}
</style>



// WEBPACK FOOTER //
// src/components/SidebarNav.vue